import { useReveal } from '@/hooks/useReveal';

const flowSteps = [
  { icon: 'ri-user-smile-line', label: 'Customer', description: 'Sends a message on WhatsApp' },
  { icon: 'ri-whatsapp-line', label: 'WhatsApp', description: 'Message received instantly' },
  { icon: 'ri-settings-3-line', label: 'Automation', description: 'AI & workflows trigger' },
  { icon: 'ri-database-2-line', label: 'CRM', description: 'Lead captured & organized' },
  { icon: 'ri-team-line', label: 'Sales Team', description: 'Hot lead assigned' },
  { icon: 'ri-bill-line', label: 'Invoice', description: 'Auto-generated & sent' },
  { icon: 'ri-bank-card-line', label: 'Payment', description: 'Collected on WhatsApp' },
  { icon: 'ri-star-line', label: 'Review', description: 'Auto-review request' },
  { icon: 'ri-refresh-line', label: 'Repeat', description: 'Re-engagement triggered' },
];

export default function WorkflowSection() {
  const { ref, visible } = useReveal();

  return (
    <section className="relative py-20 md:py-28 bg-background-100 overflow-hidden">
      {/* Decorative */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-primary-500/2 blur-3xl pointer-events-none"></div>

      <div ref={ref} className={`max-w-7xl mx-auto px-4 md:px-6 reveal ${visible ? 'visible' : ''}`}>
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <span className="inline-block px-4 py-1.5 rounded-full bg-violet-100 text-violet-700 text-xs font-semibold uppercase tracking-wider mb-5">
            How It Works
          </span>
          <h2 className="text-3xl md:text-5xl font-bold font-heading text-foreground-950 mb-5 leading-tight">
            Your Customer Journey —{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-accent-500">
              Fully Automated
            </span>
          </h2>
          <p className="text-lg text-foreground-600 leading-relaxed max-w-2xl mx-auto">
            Watch how a single WhatsApp message flows through DGFlow&apos;s intelligent automation engine
            and becomes revenue — without anyone touching a button.
          </p>
        </div>

        {/* Workflow Flow */}
        <div className="relative max-w-5xl mx-auto">
          {/* Horizontal connecting line - desktop */}
          <div className="hidden lg:block absolute top-10 left-[8%] right-[8%] h-0.5 bg-gradient-to-r from-primary-200 via-accent-200 to-primary-200"></div>

          <div className="grid grid-cols-3 lg:grid-cols-9 gap-3 md:gap-4">
            {flowSteps.map((step, i) => (
              <div key={step.label} className="flex flex-col items-center text-center group">
                {/* Step number */}
                <span className="text-[10px] font-bold text-foreground-300 mb-2 tracking-wider">0{i + 1}</span>

                {/* Icon circle */}
                <div className="relative w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-background-50 border-2 border-background-200 flex items-center justify-center mb-3 group-hover:border-primary-300 group-hover:scale-110 transition-all duration-400 z-10">
                  <i className={`${step.icon} text-xl md:text-2xl text-foreground-500 group-hover:text-primary-500 transition-colors`}></i>
                  {/* Animated ring */}
                  <div className="absolute inset-0 rounded-2xl border-2 border-primary-300/0 group-hover:border-primary-300/30 scale-110 opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
                </div>

                {/* Label */}
                <p className="text-xs md:text-sm font-bold font-heading text-foreground-900 mb-1">{step.label}</p>
                <p className="text-[10px] md:text-xs text-foreground-500 leading-tight">{step.description}</p>

                {/* Arrow between steps (mobile/tablet) */}
                {i < flowSteps.length - 1 && (
                  <div className="lg:hidden mt-2">
                    <i className="ri-arrow-down-s-line text-foreground-300"></i>
                  </div>
                )}

                {/* Arrow between steps (desktop) */}
                {i < flowSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-10 right-0 translate-x-1/2 -translate-y-1/2 z-20">
                    <i className="ri-arrow-right-s-line text-foreground-300 text-sm"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}